import { PreviewCanvas } from 'pages';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PreviewTabsCard from './PreviewTabsCard';
import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { downloadModel, processModelByFormat } from 'store/actions/rpc';
import { DownloadFormatsModal } from 'pages/Home/Models/DownloadFormatsModal';
import { getSelectedModel, getSelectedModels, updateModelPreviewImage } from 'store/actions/userData';
import { buildAuthUrl } from 'navigation/navigationHelpers';
import { useCookies } from 'react-cookie';
import { redirectToWordpressProduct } from 'store/actions/app';
import {
  getPreviewImage,
  normalizeSuggestedModels,
  toArray,
  buildMetadataRows,
  buildStats,
} from './utils';
import _ from 'lodash';
import constants from 'constants/index';
import SuggestedModelsSection from './SuggestedModelsSection';

import {
  PageWrapper,
  PageShell,
  Content,
  ViewerColumn,
  ViewerSurface,
  ViewerTopBar,
  ViewerHeading,
  ViewerLabel,
  ViewerMetaGroup,
  StatusCluster,
  StatusPill,
  ViewerStage,
  StageBadgeRow,
  ViewerStageInner,
  ViewerPlaceholder,
  StageOverlay,
  StageFooter,
  TagsWrap,
  FormatTag,
  EmptyState,
  Sidebar,
  SidebarInner,
  SidebarImageBox,
  SidebarImage,
  SidebarImagePlaceholder,
  SidebarHeader,
  Overline,
  Title,
  CreatorBadge,
  ButtonStack,
  ActionButton,
  PrimeActionButton,
  ActionsInline,
  Divider,
  SectionTitle,
  StatusButton,
} from './styles';
import { updateCanvas } from 'utils';
import { ratios } from 'pages/ModelCanvas/SideMenus/Camera';
import { PreviewAnimations } from './PreviewAnimations';
import { usePreventPageScroll } from 'components/hooks';

const { CANVAS_UPDATE_TYPES, CANVAS_UPDATE_METHODS, CONTEXT_VALUES } = constants;

const PreviewModel = withTheme(() => {
  const [ previewURL, setPreviewURL ] = useState(null);
  const [ isPreviewLoading, setIsPreviewLoading ] = useState(false);
  const [ updatingPreview, setUpdatingPreview ] = useState(false);
  const [ thumbnailOverride, setThumbnailOverride ] = useState(null);
  const [ shouldSaveThumbnail, setShouldSaveThumbnail ] = useState(false);
  const [ copied, setCopied ] = useState(false);
  const [ downloadModal, setDownloadModal ] = useState(false);
  const viewerRef = useRef(null);
  const stageRef = useRef(null);
  const canvasStore = useSelector(state => state.canvas);
  const serializedData = useMemo(() => canvasStore.serializedData, [ canvasStore ]);
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const [ searchParams ] = useSearchParams();
  const [ cookies ] = useCookies([ 'userAuth' ]);
  const rpc_guid = searchParams.get('rpc_guid') ?? null;
  const token = cookies.archvision_token;
  const userId = userDataStore?.data?.userId;
  const selectedModel = userDataStore?.selectedModel || null;
  const selectedModels = userDataStore?.selectedModels || [];
  const screenshotData = serializedData?.metadata?.screenshotData;

  const teamId = userDataStore?.selectedTeamId || (userDataStore?.data?.teams?.[0]?.teamId) || null;
  const userTeams = useMemo(() => userDataStore.data?.teams || [], [ userDataStore.data?.teams ]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const onCreators = location.search.includes(CONTEXT_VALUES.CREATORS);
  const onTeams = location.search.includes(CONTEXT_VALUES.TEAM);
  const onMyFiles = location.search.includes(CONTEXT_VALUES.USER);
  const onAdmin = location.search.includes(CONTEXT_VALUES.ADMIN);

  const title = selectedModel?.title || 'Untitled model';
  const creator = selectedModel?.creator || 'ArchVision';
  const category = selectedModel?.category;
  const subcategory = selectedModel?.subcategory;
  const tags = toArray(selectedModel?.tags).filter(Boolean);
  const formats = selectedModel?.formats || {};
  // TODO: this only checks that someone is signed in, not that they are entitled to this model.
  // The gate should be the subscription, but the server does not give us a dependable flag yet —
  // can_download is not populated across every context. Left permissive until the API provides one.
  const canDownload = userId;

  const suggestedModels = useMemo(() => {
    return normalizeSuggestedModels(selectedModels, rpc_guid);
  }, [ selectedModels ]);

  usePreventPageScroll(stageRef, Boolean(previewURL));

  const selectedTeamName = useMemo(() => {
    return userTeams.find(team => team.teamId === teamId)?.teamName || '';
  }, [ userTeams, teamId ]);

  useEffect(() => {
    setThumbnailOverride(null);
    setShouldSaveThumbnail(false);
    setUpdatingPreview(false);
  }, [ rpc_guid ]);

  const updateModelPreview = useCallback(() => {
    if (!shouldSaveThumbnail || _.isEmpty(screenshotData) || !selectedModel?.rpc_guid) {
      return;
    }

    setUpdatingPreview(true);

    const metadataArgs = {
      token,
      guid: selectedModel.rpc_guid,
      payload: { previewImage: screenshotData },
      userId,
      onComplete: () => {
        setUpdatingPreview(false);
        setThumbnailOverride(screenshotData);
        setShouldSaveThumbnail(false);

        dispatch(getSelectedModel({
          id: selectedModel.rpc_guid,
          userId,
          token,
          teamId,
        }));
      },
    };

    dispatch(updateModelPreviewImage(metadataArgs));
  }, [ dispatch, shouldSaveThumbnail, screenshotData, selectedModel?.rpc_guid, token, userId, teamId ]);

  useEffect(() => {
    updateModelPreview();
  }, [ updateModelPreview ]);


  const handleMetadataSaved = useCallback(() => {
    const modelId = selectedModel?.rpc_guid || rpc_guid;

    if (!modelId) {
      return;
    }

    dispatch(getSelectedModel({
      id: modelId,
      userId,
      token,
      teamId,
    }));
  }, [ dispatch, selectedModel?.rpc_guid, rpc_guid, userId, token, teamId ]);

  const availableFormats = useMemo(() => {
    return Object.entries(formats)
      .filter(([ , value ]) => value === 'AVAILABLE')
      .map(([ key ]) => key);
  }, [ formats ]);

  const thumbnail = useMemo(() => {
    if (thumbnailOverride) {
      return thumbnailOverride;
    }

    if (selectedModel?.rpc_guid !== rpc_guid) {
      return null;
    }

    return getPreviewImage(selectedModel, rpc_guid);
  }, [ thumbnailOverride, selectedModel, rpc_guid ]);

  const loadPreview = useCallback(() => {
    if (!rpc_guid) {
      return Promise.resolve(null);
    }

    setIsPreviewLoading(true);

    const onComplete = () => {
      dispatch({ type: 'ON_COMPLETE' });
    };

    const onError = error => {
      console.error('Error occurred while downloading model.', error);
      setPreviewURL(null);
    };

    const args = {
      rpcGuid: rpc_guid,
      format: 'DRACO_GLB',
      token: null,
      showModal: false,
      onComplete,
      onError,
    };

    return dispatch(downloadModel(args))
      .then(url => {
        setPreviewURL(url || null);
        return url;
      })
      .catch(error => {
        console.error(error);
        setPreviewURL(null);
        return null;
      })
      .finally(() => {
        setIsPreviewLoading(false);
      });
  }, [ dispatch, rpc_guid ]);

  useEffect(() => {
    loadPreview();
  }, [ loadPreview ]);

  const normalizedStats = useMemo(() => buildStats(serializedData), [ serializedData ]);

  const metadataRows = useMemo(() => {
    const baseRows = buildMetadataRows({ selectedModel });

    if (onTeams) {
      return [
        ...baseRows,
        { label: 'Team', value: selectedTeamName || '—' },
        { label: 'Published by', value: selectedModel?.user_email || '—' },
      ];
    }
    else if (onAdmin) {
      return [
        ...baseRows,
        { label: 'GUID', value: selectedModel?.rpc_guid || rpc_guid || '—' },
        { label: 'Published by', value: selectedModel?.user?.email || '—' },
      ];
    }

    return baseRows;
  }, [ selectedModel, onTeams, selectedTeamName ]);

  const handleWordpressRedirect = useCallback(productId => {
    const args = {
      productId,
      openNew: true,
    };

    dispatch(redirectToWordpressProduct(args));
  }, [ dispatch ]);

  const handleOnDownload = useCallback((format, onComplete) => {
    if (!userId) {
      window.location.href = buildAuthUrl();
      return;
    }

    const args = {
      rpcGuid: selectedModel?.rpc_guid || rpc_guid,
      format,
      token,
      download: true,
      onComplete,
    };

    dispatch(downloadModel(args));
  }, [ dispatch, token, userId, selectedModel?.rpc_guid, rpc_guid ]);

  // The formats modal hands callbacks in an object; the preview download helper takes them positionally.
  const handleFormatDownload = useCallback((format, callbacks) => {
    handleOnDownload(format, callbacks?.onComplete);
  }, [ handleOnDownload ]);

  const handleFormatPublish = useCallback((format, callbacks) => {
    const args = {
      rpcGuid: selectedModel?.rpc_guid || rpc_guid,
      format,
      token,
      onError: callbacks?.onError,
    };

    dispatch(processModelByFormat(args));
  }, [ dispatch, token, selectedModel?.rpc_guid, rpc_guid ]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(
        `https://preview.archvision.app/fovea/${rpc_guid}`,
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error(error);
    }
  };

 const handleSuggestedModelClick = useCallback(model => {
  const nextRpcGuid = model.rpc_guid;

  if (!nextRpcGuid || nextRpcGuid === rpc_guid) {
    return;
  }

  const nextSearchParams = new URLSearchParams(location.search);
  nextSearchParams.set('rpc_guid', nextRpcGuid);

  window.location.href = `${location.pathname}?${nextSearchParams.toString()}`;
}, [ location.pathname, location.search, rpc_guid ]);

  useEffect(() => {
    if (!rpc_guid) return;

    dispatch(getSelectedModel({
      id: rpc_guid,
      userId,
      token,
      teamId,
    }));
  }, [ dispatch, rpc_guid, token, userId, teamId ]);

  useEffect(() => {
    if (!selectedModel) {
      return;
    }

    dispatch(getSelectedModels({
      userId,
      token,
      teamId,
      creator: selectedModel?.creator,
      category: selectedModel?.category,
      subcategory: selectedModel?.subcategory,
      tags: selectedModel?.tags,
    }));
  }, [ dispatch, selectedModel, token, userId, teamId ]);
  return (
    <PageWrapper $selectedTheme={selectedTheme}>
      <PageShell>
        <Content>
          <ViewerColumn>
            <ViewerSurface ref={viewerRef}>
              <ViewerTopBar>
                <ViewerHeading>
                  <ViewerLabel>Asset Preview</ViewerLabel>
                </ViewerHeading>

                <ViewerMetaGroup>
                  <StatusCluster>
                    <StatusPill $accent>{availableFormats.length || 0} formats ready</StatusPill>
                  </StatusCluster>
                </ViewerMetaGroup>
              </ViewerTopBar>

              <ViewerStage>
                <StageBadgeRow>
                  <StatusCluster>
                    {!previewURL && (
                      <StatusButton enabled={!isPreviewLoading} onClick={loadPreview}>
                        {isPreviewLoading ? 'Reloading...' : 'Reload preview'}
                      </StatusButton>
                    )}
                  </StatusCluster>
                </StageBadgeRow>

                {previewURL ? (
                  <ViewerStageInner ref={stageRef}>
                    <PreviewCanvas previewURL={previewURL} />
                  </ViewerStageInner>
                ) : (
                  <ViewerPlaceholder>
                    Loading the optimized preview model for the FOVEA viewer. Once ready, this space becomes
                    the main interactive canvas for orbit, inspect, and preview workflows.
                  </ViewerPlaceholder>
                )}
                <StageOverlay />
                <StageFooter>
                  <PreviewAnimations />
                  {onMyFiles && (
                    <StatusButton
                      enabled={!updatingPreview}
                      onClick={() => {
                        setShouldSaveThumbnail(true);

                        const updateArgs = {
                          dispatch,
                          type: CANVAS_UPDATE_TYPES.CAMERA,
                          method: CANVAS_UPDATE_METHODS.UPDATE,
                        };

                        const inboundData = {
                          takeScreenshot: true,
                          aspectRatio: ratios.broadcast.value,
                          imageDataOnly: true,
                        };

                        updateCanvas(updateArgs, inboundData);
                      }}
                    >
                      {updatingPreview ? 'Updating...' : 'Update thumbnail'}
                    </StatusButton>
                  )}
                </StageFooter>
              </ViewerStage>
            </ViewerSurface>
            <PreviewTabsCard
              selectedModel={selectedModel}
              metadataRows={metadataRows}
              tags={tags}
              normalizedStats={normalizedStats}
              availableFormats={availableFormats}
              onDownload={handleOnDownload}
              onMyFiles={onMyFiles}
              onMetadataSaved={handleMetadataSaved}
            />
          </ViewerColumn>

          <Sidebar>
            <SidebarInner>
              <SidebarImageBox>
                {thumbnail ? (
                  <SidebarImage src={thumbnail} alt={title} />
                ) : (
                  <SidebarImagePlaceholder>
                    Cover image area for the model.
                    This can later support hero stills, thumbnails, or branded product art.
                  </SidebarImagePlaceholder>
                )}
              </SidebarImageBox>
              <SidebarHeader>
                <Overline>Model Overview</Overline>
                <Title selectedTheme={selectedTheme}>{title}</Title>
                <ViewerHeading>
                  <CreatorBadge>
                    {creator}
                  </CreatorBadge>
                  {category && <CreatorBadge>{category}</CreatorBadge>}
                  {subcategory && <CreatorBadge>{subcategory}</CreatorBadge>}
                </ViewerHeading>
              </SidebarHeader>
              <ButtonStack>
                <PrimeActionButton
                  onClick={() => (canDownload
                    ? setDownloadModal(true)
                    : handleWordpressRedirect(selectedModel?.product_id))}
                  $selectedTheme={selectedTheme}
                >
                  {canDownload ? 'Download' : 'Purchase Now'}
                </PrimeActionButton>
                <ActionsInline>
                  <ActionButton
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/${onCreators ? 'creator-' : ''}${onTeams ? 'team-' : ''}models/${rpc_guid}`);
                    }}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton type="button" onClick={handleShare}>
                    {copied ? 'Copied' : 'Share'}
                  </ActionButton>
                </ActionsInline>
              </ButtonStack>
              <Divider />
              {!userId && (
                <section>
                  <SectionTitle>Formats Available with Purchase</SectionTitle>
                  {availableFormats.length && onCreators ? (
                    <TagsWrap>
                      {availableFormats.map(format => (
                        <FormatTag key={format}>{format}</FormatTag>
                      ))}
                    </TagsWrap>
                  ) : (
                    <EmptyState>Format chips appear here when the model exposes downloadable outputs.</EmptyState>
                  )}
                  <Divider />
                </section>
              )}
              <section>
                <SuggestedModelsSection
                  models={suggestedModels}
                  onModelClick={handleSuggestedModelClick}
                  hideActionButton={true}
                />
              </section>
            </SidebarInner>
          </Sidebar>
        </Content>
      </PageShell>

      {/* Mounted at the page root: SidebarInner has a backdrop-filter, which makes it the
          containing block for fixed positioning and would trap the modal inside that column. */}
      {canDownload && (
        <DownloadFormatsModal
          visible={downloadModal}
          formats={formats}
          rpcGuid={selectedModel?.rpc_guid || rpc_guid}
          canPublish={onMyFiles}
          onClose={() => setDownloadModal(false)}
          onDownload={handleFormatDownload}
          onPublish={handleFormatPublish}
        />
      )}
    </PageWrapper>
  );
});

export { PreviewModel };
